import { __ } from '@wordpress/i18n';
import { Button } from '@wordpress/components';

const AddSection = ({ getSectionItems, setSectionItems, setAttributes, attributes }) => {
    // Get deleted sections data from attributes (you'll need to add this to your block attributes)
    const { deletedSectionsData = {}, style } = attributes || {};

    const getDefaultItems = (type) => {
        switch (type) {
            case 'about':
                return {
                    description: '',
                    dateOfBirth: '',
                    nationality: ''
                };
            case 'contact':
                return {
                    phoneNumber: '',
                    email: '',
                    website: '',
                    address: '',
                    country: '',
                    city: '',
                    postalCode: '',
                    socialIcon: []
                };
            case 'skills':
                return [{ name: 'New Skill', percentage: 70 }];
            case 'languages':
                return [{ name: 'New Language', percentage: 70 }];
            case 'hobbies':
                return [{ name: 'New Hobby', icon: 'Music' }];
            case 'education':
                return [
                    {
                        id: Date.now(),
                        institution: '',
                        name: '',
                        degree: '',
                        city: '',
                        fromDate: '',
                        toDate: '',
                        country: '',
                        current: false,
                        cgpa: '',
                        cgpaValue: '',
                        educationDescription: '',
                    },
                ];
            case 'experience':
                return [
                    {
                        id: Date.now(),
                        name: '',
                        designation: '',
                        present: false,
                        fromExpDate: '',
                        toExpDate: '',
                        experienceDescription: '',
                    },
                ];
            case 'reference':
                return [
                    {
                        id: Date.now(),
                        name: '',
                        jobPosition: '',
                        company: '',
                        phone: '',
                        relation: '',
                    },
                ];
            case 'awards':
                return [
                    {
                        id: Date.now(),
                        name: 'Top Finance Leader LATAM – Global Capital Solutions, 2022'
                    },
                ];
            case 'tools':
                return [
                    {
                        id: Date.now(),
                        name: '',
                    },
                ];
            default:
                return [];
        }
    };

    // Helper function to check if section exists
    const sectionExists = (type) => {
        const sections = getSectionItems();
        return sections.some(section => section.type === type);
    };

    const addSection = (type, options = {}) => {
        const sections = getSectionItems();

        if (sectionExists(type)) {
            return;
        }

        // Check if we have preserved data for this section type
        const preservedData = deletedSectionsData[type];
        
        let newSection;
        let insertPosition = sections.length; // Default to end
        
        if (preservedData) {
            // Restore the previously deleted section with its data
            newSection = {
                ...preservedData.sectionData,
                id: Date.now().toString(), // Generate new ID to avoid conflicts
            };
            
            // Use the preserved position, but ensure it's within bounds
            insertPosition = Math.min(preservedData.position, sections.length);
            
            // Remove this section from deleted sections data
            const updatedDeletedSectionsData = { ...deletedSectionsData };
            delete updatedDeletedSectionsData[type];
            setAttributes({ deletedSectionsData: updatedDeletedSectionsData });
        } else {
            // Create new section with default data
            newSection = {
                id: Date.now().toString(),
                type,
                title: options.title || type.charAt(0).toUpperCase() + type.slice(1),
                items: getDefaultItems(type),
                ...options,
            };
        }

        // Insert section at the correct position
        const updatedSections = [...sections];
        updatedSections.splice(insertPosition, 0, newSection);
        setSectionItems(updatedSections);
    };

    return (
        <>
            <div className="bwdtm-btn-wrapper">
                <div className="bwdtm-add-section-btns">
                    {
                        style !== 'style24' && (
                            <Button
                            className="bwdtm-add-btn"
                            disabled={sectionExists('about')}
                            onClick={() =>
                                addSection('about', {
                                    title: 'About Me',
                                    description: '',
                                    dateOfBirth: '',
                                    nationality: ''
                                })
                            }
                        >
                            {__('Add About Section', 'cv-builder')}
                            <span className="bwdcv-add-section-i">+</span>
                        </Button>
                        )
                    }                    
                    {
                        (style !== 'style25' && style !== 'style24') && (
                            <Button
                            className="bwdtm-add-btn"
                            disabled={sectionExists('contact')}
                            onClick={() =>
                                addSection('contact', {
                                    title: 'Contact',
                                    phoneNumber: '+1234567890',
                                    email: 'info@example.com',
                                    website: 'www.example.com',
                                    address: '',
                                    country: '',
                                    city: '',
                                    postalCode: '',
                                    socialIcon: []
                                })
                            }
                        >
                            {__('Add Contact Section', 'cv-builder')}
                            <span className="bwdcv-add-section-i">+</span>
                        </Button>
                        )
                    }
                    <Button
                        className="bwdtm-add-btn"
                        disabled={sectionExists('skills')}
                        onClick={() =>
                            addSection('skills', {
                                title: 'Skills'
                            })
                        }
                    >
                        {__('Add Skills Section', 'cv-builder')}
                        <span className="bwdcv-add-section-i">+</span>
                    </Button>

                    <Button
                        className="bwdtm-add-btn"
                        disabled={sectionExists('languages')}
                        onClick={() =>
                            addSection('languages', {
                                title: 'Languages'
                            })
                        }
                    >
                        {__('Add Languages Section', 'cv-builder')}
                        <span className="bwdcv-add-section-i">+</span>
                    </Button>

                    <Button
                        className="bwdtm-add-btn"
                        disabled={sectionExists('hobbies')}
                        onClick={() =>
                            addSection('hobbies', {
                                title: 'Hobbies'
                            })
                        }
                    >
                        {__('Add Hobbies Section', 'cv-builder')}
                        <span className="bwdcv-add-section-i">+</span>
                    </Button>

                    <Button
                        className="bwdtm-add-btn"
                        disabled={sectionExists('education')}
                        onClick={() =>
                            addSection('education', {
                                title: 'Education'
                            })
                        }
                    >
                        {__('Add Education Section', 'cv-builder')}
                        <span className="bwdcv-add-section-i">+</span>
                    </Button>

                    <Button
                        className="bwdtm-add-btn"
                        disabled={sectionExists('experience')}
                        onClick={() =>
                            addSection('experience', {
                                title: 'Experience'
                            })
                        }
                    >
                        {__('Add Experience Section', 'cv-builder')}
                        <span className="bwdcv-add-section-i">+</span>
                    </Button>

                    <Button
                        className="bwdtm-add-btn"
                        disabled={sectionExists('reference')}
                        onClick={() =>
                            addSection('reference', {
                                title: 'Reference'
                            })
                        }
                    >
                        {__('Add Reference Section', 'cv-builder')}
                        <span className="bwdcv-add-section-i">+</span>
                    </Button>

                    <Button
                        className="bwdtm-add-btn"
                        disabled={sectionExists('awards')}
                        onClick={() =>
                            addSection('awards', {
                                title: 'Awards',
                                items: [
                                    { name: 'Top Finance Leader LATAM – Global Capital Solutions, 2022' },
                                    { name: 'Outstanding Account Manager – Banco Nacional, 2018' },
                                    { name: 'Reduced finance operating expenses by $250K annually' },
                                    { name: 'Successfully led post-merger finance integration project across 3 countries' },
                                ],
                            })
                        }
                    >
                        {__('Add Awards Section', 'cv-builder')}
                        <span className="bwdcv-add-section-i">+</span>
                    </Button>

                    <Button
                        className="bwdtm-add-btn"
                        disabled={sectionExists('tools')}
                        onClick={() =>
                            addSection('tools', {
                                title: 'Tools',
                                items: [
                                    { name: 'SAP, Oracle, QuickBooks, Xero' },
                                    { name: 'Microsoft Excel (Advanced), Power BI' },
                                    { name: 'Tableau, Zoho Finance Suite' },
                                    { name: 'CRM Tools: Salesforce, HubSpot' },
                                    { name: 'Banking platforms & compliance tools' },
                                ],
                            })
                        }
                    >
                        {__('Add Tools Section', 'cv-builder')}
                        <span className="bwdcv-add-section-i">+</span>
                    </Button>
                </div>
            </div>
        </>
    );
};

export default AddSection;